/**
 * ============================================================================
 * BlogConnectModalForm — Model (form values + RHF resolver)
 * ============================================================================
 * Explicit resolver matching pre-MVC Blog validators.
 * No Yup. Degree/program only required when requireProgramFields is true.
 * ============================================================================
 */

import type { FieldErrors, Resolver } from "react-hook-form";
import { SELECT_DEGREE, SELECT_PROGRAM } from "../utils";

export interface BlogConnectModalFormValues {
  fullName: string;
  countryCode: string;
  phoneNumber: string;
  emailId: string;
  degree: string;
  program: string;
  isTermsAccepted: boolean;
}

export const CODE = "Code";
export const NATIONAL_CODE = "+91";

export const degreeOptions = ["UG", "PG"];

export const errorAccordingToFieldName: Record<string, string> = {
  fullName: "Please enter valid full name",
  countryCode: "Please enter valid country code",
  phoneNumber: "Please enter valid phone number",
  emailId: "Please enter valid email address",
  degree: "Please select a degree",
  program: "Please select a program",
  isTermsAccepted: "Please accept terms & conditions",
};

/** Blog-specific regexes (match pre-MVC utils.ts). */
export const regexForInputs: Record<string, RegExp> = {
  fullName: /^[a-zA-Z][a-zA-Z ]{2,49}$/,
  countryCode: /^\+(?:[0-9]?){6,14}[0-9]$/,
  phoneNumberIndia: /^[1-9][0-9]{9}$/,
  phoneNumberIntl: /^[1-9][0-9]{6,14}$/,
  emailId:
    /^(?!.*\.\.)[a-zA-Z0-9](\.?[a-zA-Z0-9_-]+)*@[a-zA-Z0-9-]+(\.[a-zA-Z0-9-]+)*\.[a-zA-Z]{2,}$/,
};

export interface CreateBlogConnectModalFormResolverOptions {
  /**
   * When true, degree/program are required.
   * False on new-lead first screen before programs are revealed.
   */
  requireProgramFields: boolean;
}

/**
 * Explicit RHF resolver — controlled inputs via setValue/watch.
 * Degree/program only required when requireProgramFields is true.
 */
export function createBlogConnectModalFormResolver(
  options: CreateBlogConnectModalFormResolverOptions,
): Resolver<BlogConnectModalFormValues> {
  const { requireProgramFields } = options;

  return (values) => {
    const errors: FieldErrors<BlogConnectModalFormValues> = {};

    if (!values.fullName || !regexForInputs.fullName.test(values.fullName)) {
      errors.fullName = {
        type: "validate",
        message: errorAccordingToFieldName.fullName,
      };
    }

    if (
      !values.countryCode ||
      values.countryCode === CODE ||
      !regexForInputs.countryCode.test(values.countryCode)
    ) {
      errors.countryCode = {
        type: "validate",
        message: errorAccordingToFieldName.countryCode,
      };
    }

    const phoneRegex =
      values.countryCode !== NATIONAL_CODE
        ? regexForInputs.phoneNumberIntl
        : regexForInputs.phoneNumberIndia;
    if (!values.phoneNumber || !phoneRegex.test(String(values.phoneNumber))) {
      errors.phoneNumber = {
        type: "validate",
        message: errorAccordingToFieldName.phoneNumber,
      };
    }

    if (!values.emailId || !regexForInputs.emailId.test(values.emailId)) {
      errors.emailId = {
        type: "validate",
        message: errorAccordingToFieldName.emailId,
      };
    }

    if (values.isTermsAccepted !== true) {
      errors.isTermsAccepted = {
        type: "validate",
        message: errorAccordingToFieldName.isTermsAccepted,
      };
    }

    if (requireProgramFields) {
      if (
        values.degree === SELECT_DEGREE ||
        !degreeOptions.includes(values.degree)
      ) {
        errors.degree = {
          type: "validate",
          message: errorAccordingToFieldName.degree,
        };
      }
      if (!values.program || values.program === SELECT_PROGRAM) {
        errors.program = {
          type: "validate",
          message: errorAccordingToFieldName.program,
        };
      }
    }

    const hasErrors = Object.keys(errors).length > 0;
    return hasErrors
      ? { values: {} as Record<string, never>, errors }
      : { values, errors: {} };
  };
}

export interface BlogConnectModalFormProps {
  setMessage?: any;
  setFormResponse?: (x: any) => void;
  gaOtherPayloadInfo?: any;
  setFormValuesData?: (x: any) => void;
  associatedPrograms?: Array<ProgramDetails>;
  isNewLeadForm?: boolean;
  checkLeadFail?: (x: boolean) => void;
  isLambdaAPI?: boolean;
}
